//@ts-check
/// <reference path="../../../state/rdms/types.js" />

import { useCallback, useMemo } from "react";
import { generateColorGradient } from "../../../utils/colors.utils";

/**
 * @param {{ options: Array<{ label: string, onClick: () => void, show: boolean }>, data: Rdm | null }} props
 * @returns {{ visibleOptions: Array<{ label: string, onClick: () => void, show: boolean }>, remapUsers: (RdmUser & { color: string, percent: number })[], getPercentPositives: (user: RdmUser) => number, orderedUsersByPositives: RdmUser[], getColorGradient: () => string[] }}
 */
export function useUserPanel({ options, data }) {
  const visibleOptions = options.filter((o) => o.show !== false);
  const getPercentPositives = useCallback(
    /**
     * @param {RdmUser} user
     * @return {number}
     */
    (user) => {
      const total = user.positives + user.negatives;
      if (total === 0) {
        return 0;
      }
      return (user.positives / total) * 100;
    },
    []
  );

  const orderedUsersByPositives = useMemo(() => {
    if (!data?.rdmUsers) {
      return [];
    }

    return [...data.rdmUsers].sort((a, b) => {
      const percentA = getPercentPositives(a);
      const percentB = getPercentPositives(b);

      if (percentB !== percentA) {
        return percentB - percentA;
      }

      if (b.positives !== a.positives) {
        return b.positives - a.positives;
      }

      if (a.negatives !== b.negatives) {
        return a.negatives - b.negatives;
      }

      return a.userData.username.localeCompare(b.userData.username);
    });
  }, [data?.rdmUsers, getPercentPositives]);

  const getColorGradient = useCallback(() => {
    return generateColorGradient(
      "#118052",
      "#d91515",
      orderedUsersByPositives.length,
      0.3
    );
  }, [orderedUsersByPositives]);

  const remapUsers = useCallback(() => {
    return orderedUsersByPositives.map((user, idx) => ({
      ...user,
      color: getColorGradient()[idx],
      percent: getPercentPositives(user),
    }));
  }, [orderedUsersByPositives, getColorGradient, getPercentPositives]);

  return {
    visibleOptions,
    remapUsers: remapUsers(),
    getPercentPositives,
    orderedUsersByPositives,
    getColorGradient,
  };
}
